import { supabase } from '../lib/supabase'
import type { CallSheet, Deliverable, Project } from '../types/portal'
import { normaliseStatus } from './deliverables'
import { fetchVersionHistory, type StoredVersions } from './versionStore'
import { fetchTranscriptsForProject, type StoredTranscript } from './transcriptStore'

/**
 * Loads the genuine projects and deliverables from Supabase.
 *
 * The database holds each project's identity (client, name, slug), its shoot
 * details, and its deliverables — key, title, version, status, visibility,
 * order, the Drive review link, the audio link and the video framing. Fields
 * the schema does not carry start blank and are maintained in the session.
 * Nothing is invented, and no rows are written or modified here.
 */

const CALL_SHEET_BUCKET = 'call-sheets'

function warn(what: string, error: { message: string } | null) {
  if (error) console.warn(`[supabase] ${what} failed: ${error.message}`)
}

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

/** The project-level shoot information, as saved by staff. */
function storedSchedule(row: Record<string, unknown>) {
  return {
    nextShootDate: text(row.next_shoot_date).slice(0, 10),
    nextShootTime: text(row.next_shoot_time),
    location: text(row.shoot_location),
  }
}

/**
 * The stored call sheet, if one has been uploaded. The public URL is derived
 * from the stored path on every load rather than itself being stored, so it
 * can never point at a domain or bucket that has since changed.
 */
function storedCallSheet(row: Record<string, unknown>): CallSheet | undefined {
  const path = text(row.call_sheet_path)
  if (!path || !supabase) return undefined
  return {
    fileName: text(row.call_sheet_file_name) || 'Call Sheet.pdf',
    path,
    url: supabase.storage.from(CALL_SHEET_BUCKET).getPublicUrl(path).data.publicUrl,
  }
}

function storedDeliverable(row: Record<string, unknown>): Deliverable {
  return {
    id: String(row.deliverable_key ?? ''),
    name: String(row.title ?? 'Untitled'),
    type: (String(row.category ?? 'Video') || 'Video') as Deliverable['type'],
    quantity: '',
    /* Legacy stored values are mapped forward, so old rows stay valid. */
    status: normaliseStatus(String(row.status ?? '')),
    version: String(row.version ?? ''),
    versionDate: '—',
    runtime: '—',
    /* What the client's player and files actually read from. */
    driveUrl: text(row.drive_url),
    audioUrl: text(row.audio_url),
    videoFormat: (text(row.video_format) || undefined) as Deliverable['videoFormat'],
    internalNote: '',
    /* Which document a transcript came from, and its length. Staff only. */
    transcriptFilename: text(row.transcript_source_filename) || undefined,
    transcriptWordCount:
      typeof row.transcript_word_count === 'number' ? row.transcript_word_count : undefined,
    visibleToClient: Boolean(row.visible_to_client),
    notes: [],
    changeRequests: [],
    versions: [],
  }
}

/** The database's deliverable id → the key the interface uses. */
function keyMap(rows: Record<string, unknown>[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const row of rows) {
    if (row.id && row.deliverable_key) map.set(String(row.id), String(row.deliverable_key))
  }
  return map
}

/**
 * Applies the stored version history, the current version's publication date,
 * and the stored transcript with its saved states.
 *
 * The transcript is applied here so it arrives with the deliverable on every
 * route — the client link included — rather than depending on a later pass.
 */
function withStored(
  item: Deliverable,
  versions: Map<string, StoredVersions>,
  transcripts: Map<string, StoredTranscript>,
): Deliverable {
  const stored = versions.get(item.id)
  const transcript = transcripts.get(item.id)

  return {
    ...item,
    ...(stored
      ? { versions: stored.history, versionDate: stored.currentDate || item.versionDate }
      : {}),
    ...(transcript
      ? { transcript: transcript.text, transcriptRevisions: transcript.revisions }
      : {}),
  }
}

/**
 * One project by its real id — used on the client side once join_project has
 * granted access. Reads the project's identity and its deliverables directly,
 * so nothing depends on being able to list every project.
 */
export async function fetchProjectById(projectId: string): Promise<Project | null> {
  if (!supabase || !projectId) return null

  /* Selected in full so the shoot columns are picked up wherever they exist,
     without the query failing on a schema that predates them. */
  const { data: row, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .maybeSingle()
  warn('client project load', error)
  if (!row) return null

  /* Selected in full for the same reason as the project row: a schema without
     the review columns still loads. */
  const { data: deliverableRows, error: deliverableError } = await supabase
    .from('deliverables')
    .select('*')
    .eq('project_id', projectId)
    .order('sort_order', { ascending: true })
  warn('client deliverable load', deliverableError)

  const rows = (deliverableRows ?? []) as Record<string, unknown>[]
  const keys = keyMap(rows)
  /* Version history and transcripts load with the project, so the client link
     renders exactly the same stored content the studio side sees. */
  const [versions, transcripts] = await Promise.all([
    fetchVersionHistory(projectId, keys),
    fetchTranscriptsForProject(projectId, keys),
  ])

  return {
    id: String(row.id),
    slug: String(row.slug ?? ''),
    client: String(row.client_name ?? ''),
    name: String(row.project_name ?? ''),
    type: '',
    description: '',
    status: String(row.status ?? ''),
    ...storedSchedule(row),
    callSheet: storedCallSheet(row),
    clientContact: '',
    producer: '',
    updated: '',
    deliverables: rows.map((item) => withStored(storedDeliverable(item), versions, transcripts)),
    shoots: [],
    team: [],
    archived: String(row.status ?? 'active') === 'archived',
  }
}

/** One project by its link slug — used when a client opens a project link. */
export async function fetchProjectBySlug(slug: string): Promise<Project | null> {
  const all = await fetchProjects()
  return all.find((item) => item.slug === slug) ?? null
}

export async function fetchProjects(): Promise<Project[]> {
  if (!supabase) return []

  const { data: projectRows, error } = await supabase.from('projects').select('*')
  warn('project list', error)
  if (!projectRows?.length) return []

  const { data: deliverableRows, error: deliverableError } = await supabase
    .from('deliverables')
    .select('*')
    .order('sort_order', { ascending: true })
  warn('deliverable list', deliverableError)

  const allRows = (deliverableRows ?? []) as Record<string, unknown>[]

  /* Version history per project, so each deliverable carries its real
     publication dates and the links its earlier cuts were reviewed on. */
  const versionsByProject = new Map<string, Map<string, StoredVersions>>()
  const transcriptsByProject = new Map<string, Map<string, StoredTranscript>>()
  await Promise.all(
    projectRows.map(async (row: Record<string, unknown>) => {
      const id = String(row.id)
      const rows = allRows.filter((item) => String(item.project_id) === id)
      if (rows.length === 0) return
      const keys = keyMap(rows)
      const [versions, transcripts] = await Promise.all([
        fetchVersionHistory(id, keys),
        fetchTranscriptsForProject(id, keys),
      ])
      versionsByProject.set(id, versions)
      transcriptsByProject.set(id, transcripts)
    }),
  )

  return projectRows.map((row: Record<string, unknown>) => {
    const id = String(row.id)
    const versions = versionsByProject.get(id) ?? new Map<string, StoredVersions>()
    const transcripts = transcriptsByProject.get(id) ?? new Map<string, StoredTranscript>()
    const items = allRows
      .filter((item) => String(item.project_id) === id)
      .map((item) => withStored(storedDeliverable(item), versions, transcripts))

    return {
      id,
      slug: String(row.slug ?? ''),
      client: String(row.client_name ?? ''),
      name: String(row.project_name ?? ''),
      type: '',
      description: '',
      status: '',
      ...storedSchedule(row),
      callSheet: storedCallSheet(row),
      clientContact: '',
      producer: '',
      updated: '',
      deliverables: items,
      shoots: [],
      team: [],
      archived: String(row.status ?? 'active') === 'archived',
    }
  })
}
